"use client";

import { useProUser } from "@/contexts/pro-user-context";
import { env } from "@/env.mjs";
import { useUser } from "@/hooks/useUser";
import { GrowthBook, configureCache } from "@growthbook/growthbook-react";
import { GrowthBookProvider } from "@growthbook/growthbook-react";
import { track } from "@masterkey/analytics/client";
import { useEffect } from "react";

// Configure cache settings before creating GrowthBook instance
configureCache({
  // Consider features stale after 1 minute
  staleTTL: 1000 * 60,
  // Cache features for 24 hours
  maxAge: 1000 * 60 * 60 * 24,
  // For Remote Eval only - limit the number of cache entries (~1 entry per user)
  maxEntries: 10,
  // When false, we add a visibilitychange listener to disable SSE when the page is idle
  disableIdleStreams: false,
  // Consider a page "idle" when it is hidden for 20 seconds
  idleStreamInterval: 20000,
});

// Create GrowthBook instance
const growthbook = new GrowthBook({
  apiHost: "https://cdn.growthbook.io",
  clientKey: env.NEXT_PUBLIC_GROWTHBOOK_CLIENT_KEY,
  enableDevMode: process.env.NODE_ENV !== "production",
  trackingCallback: (experiment, result) => {
    track({
      event: "experiment_exposure",
      source: "growthbook",
      properties: {
        experimentId: experiment.key,
        variationId: result.variationId,
        variationValue: result.value,
        inExperiment: result.inExperiment,
        hashAttribute: experiment.hashAttribute,
        hashValue: result.hashValue,
      },
    });
  },
});

export function GrowthBookWrapper({ children }: { children: React.ReactNode }) {
  const { user } = useUser();
  const { tier, isPro, isPlus, isPaid } = useProUser();

  // Initialize GrowthBook in the background
  useEffect(() => {
    console.log(
      "Initializing GrowthBook with client key:",
      env.NEXT_PUBLIC_GROWTHBOOK_CLIENT_KEY,
    );

    const initGrowthBook = async () => {
      try {
        // Initialize without blocking rendering
        await growthbook.init({
          streaming: true,
          timeout: 2000,
        });

        // Update attributes immediately after successful init
        const attributes = {
          id: user?.id,
          loggedIn: !!user,
          email: user?.email,
          tier,
          isPro,
          isPlus,
          isPaid,
        };
        growthbook.setAttributes(attributes);
      } catch (error) {
        console.error("Failed to initialize GrowthBook:", error);
        // Still allow app to function
      }
    };

    void initGrowthBook();
    // Run init only once
  }, []);

  // Update attributes when user/pro status changes
  useEffect(() => {
    const attributes = {
      id: user?.id,
      loggedIn: !!user,
      email: user?.email,
      tier,
      isPro,
      isPlus,
      isPaid,
    };
    growthbook.setAttributes(attributes);
  }, [user, tier, isPro, isPlus, isPaid]);

  // Render provider and children immediately
  return (
    <GrowthBookProvider growthbook={growthbook}>{children}</GrowthBookProvider>
  );
}
